/**
 * API client for the release-manager backend plugin.
 *
 * @packageDocumentation
 */

import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  CreateReleaseRequest,
  CreateReleaseResponse,
  CurrentVersionsResponse,
  DeploymentEnvironment,
  ListReleasesResponse,
  PipelineRunStatus,
  PublishReleaseRequest,
  PullRequestStatusResponse,
  ReleaseInfo,
  UpdateReleaseRequest,
  PipelineReleaseContext,
} from '@internal/backstage-plugin-release-manager-common';

export interface ReleasePipelineDetail {
  id: string;
  environment: DeploymentEnvironment;
  pipelineName: string;
  pipelineId: string;
  organization: string;
  project: string;
  runId?: string;
  status?: PipelineRunStatus;
  url?: string;
  startedAt?: string;
  finishedAt?: string;
}

export interface ReleaseDetailResponse {
  release: ReleaseInfo;
  pipelines: ReleasePipelineDetail[];
}

/**
 * API surface for the release-manager plugin.
 *
 * @public
 */
export interface ReleaseManagerApi {
  /**
   * List releases for a system.
   */
  listReleases(
    namespace: string,
    systemName: string,
    options?: {
      group?: string;
      status?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<ListReleasesResponse>;

  /**
   * Create a new release.
   */
  createRelease(
    namespace: string,
    systemName: string,
    group: string,
    body: CreateReleaseRequest,
  ): Promise<CreateReleaseResponse>;

  /**
   * Update editable metadata fields of an existing release.
   */
  updateRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
    body: UpdateReleaseRequest,
  ): Promise<ReleaseInfo>;

  /**
   * Delete a release and its associated Azure DevOps branches.
   */
  deleteRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<void>;

  /**
   * Publish a draft release by completing the CD Pull Request and
   * registering the CD pipeline for monitoring.
   */
  publishRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
    body: PublishReleaseRequest,
  ): Promise<void>;

  /**
   * Returns the current version per deployment group by reading each
   * group's Azure DevOps pipeline YAML spec.
   *
   * @param namespace - Entity namespace
   * @param systemName - Entity system name
   * @param pipelines - Mapping of deployment group name to pipeline name or id
   */
  getCurrentVersions(
    namespace: string,
    systemName: string,
    pipelines: Record<string, string>,
  ): Promise<CurrentVersionsResponse>;

  /**
   * Retrieves the context for a release by parsing the pipeline YAML spec.
   */
  getPipelineReleaseContext(
    namespace: string,
    systemName: string,
    pipelineId: string,
  ): Promise<PipelineReleaseContext>;

  getRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<ReleaseDetailResponse>;

  getPullRequestStatus(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<PullRequestStatusResponse>;

  requeuePolicyEvaluations(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<void>;
}

/**
 * ApiRef for the release-manager API.
 *
 * @public
 */
export const releaseManagerApiRef = createApiRef<ReleaseManagerApi>({
  id: 'plugin.release-manager.api',
});

/**
 * Default implementation of {@link ReleaseManagerApi} that talks to the
 * release-manager backend over HTTP.
 *
 * @public
 */
export class ReleaseManagerApiClient implements ReleaseManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('release-manager');
  }

  async listReleases(
    namespace: string,
    systemName: string,
    options?: {
      group?: string;
      status?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<ListReleasesResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();

    if (options?.status) {
      params.append('status', options.status);
    }
    if (options?.group) {
      params.append('group', options.group);
    }
    if (options?.limit !== undefined) {
      params.append('limit', options.limit.toString());
    }
    if (options?.offset !== undefined) {
      params.append('offset', options.offset.toString());
    }

    const query = params.toString();
    const suffix = query ? `?${query}` : '';
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/releases${suffix}`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const data = await response.json();

    // The backend returns `items` while the common type uses `releases`.
    // Normalize so callers can rely on a single field.
    return {
      releases: data.releases ?? data.items ?? [],
      total: data.total ?? 0,
      limit: data.limit ?? options?.limit ?? 20,
      offset: data.offset ?? options?.offset ?? 0,
    };
  }

  async createRelease(
    namespace: string,
    systemName: string,
    group: string,
    body: CreateReleaseRequest,
  ): Promise<CreateReleaseResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/groups/${encodeURIComponent(group)}/releases`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 409) {
        const data = await response.json().catch(() => undefined);
        throw new Error(data?.error?.message ?? 'A release with this version already exists.');
      }
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async updateRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
    body: UpdateReleaseRequest,
  ): Promise<ReleaseInfo> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/groups/${encodeURIComponent(group)}/releases/${encodeURIComponent(version)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async deleteRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/groups/${encodeURIComponent(group)}/releases/${encodeURIComponent(version)}`;

    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async publishRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
    body: PublishReleaseRequest,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/groups/${encodeURIComponent(group)}/releases/${encodeURIComponent(version)}/publish`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async getCurrentVersions(
    namespace: string,
    systemName: string,
    pipelines: Record<string, string>,
  ): Promise<CurrentVersionsResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();

    for (const [group, pipelineNameOrId] of Object.entries(pipelines)) {
      params.append('pipeline', `${group}:${pipelineNameOrId}`);
    }

    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/current-versions?${params.toString()}`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getPipelineReleaseContext(
    namespace: string,
    systemName: string,
    pipelineId: string,
  ): Promise<PipelineReleaseContext> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/pipelines/${encodeURIComponent(pipelineId)}/release-context`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  async getRelease(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<ReleaseDetailResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/groups/${encodeURIComponent(group)}/releases/${encodeURIComponent(version)}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return response.json();
  }

  async getPullRequestStatus(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<PullRequestStatusResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/groups/${encodeURIComponent(group)}/releases/${encodeURIComponent(version)}/pull-request`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw await ResponseError.fromResponse(response);
    return response.json();
  }

  async requeuePolicyEvaluations(
    namespace: string,
    systemName: string,
    group: string,
    version: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/${encodeURIComponent(namespace)}/system/${encodeURIComponent(systemName)}/groups/${encodeURIComponent(group)}/releases/${encodeURIComponent(version)}/pull-request/requeue`;
    const response = await this.fetchApi.fetch(url, { method: 'POST' });
    if (!response.ok) throw await ResponseError.fromResponse(response);
  }
}
